import { useEffect, useMemo, useState } from 'react';
import { Badge, Button, Card, EmptyState, Icon, IconButton, Input, Select, Tag, Toast } from '../../design';
import { useAuth } from '../../auth/AuthProvider';
import { ApiError } from '../../lib/api';
import { AudioPlayback } from '../../components/AudioPlayback';
import { ProgramDialog } from '../../components/ProgramDialog';
import { audioApi, audioKindIcon, downloadUrl, formatAudioDuration, formatBytes } from '../../lib/audio';
import { AUDIO_KIND_LABELS, type AudioItem, type AudioKind } from '../../lib/types';

type KindFilter = 'all' | AudioKind;

export function LibraryScreen() {
  const { user } = useAuth();
  // Role check, not isPractitioner — mirrors the canSeeClients reasoning.
  const isManager = user?.role === 'admin' || user?.role === 'practitioner';

  const [files, setFiles] = useState<AudioItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const [query, setQuery] = useState('');
  const [kindFilter, setKindFilter] = useState<KindFilter>('all');
  const [publishedOnly, setPublishedOnly] = useState(false);

  const [playingId, setPlayingId] = useState<string | null>(null);
  const [dialog, setDialog] = useState<{ mode: 'create' } | { mode: 'edit'; file: AudioItem } | null>(null);

  async function load() {
    try {
      const res = await audioApi.list();
      setFiles(res.files);
    } catch {
      setError('Could not load the library.');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
  }, []);

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return files.filter((f) => {
      if (kindFilter !== 'all' && f.kind !== kindFilter) return false;
      if (publishedOnly && !f.published) return false;
      if (!q) return true;
      return f.title.toLowerCase().includes(q) || (f.description ?? '').toLowerCase().includes(q);
    });
  }, [files, query, kindFilter, publishedOnly]);

  const playing = files.find((f) => f.id === playingId) ?? null;

  async function togglePublish(file: AudioItem) {
    const next = !file.published;
    setFiles((fs) => fs.map((f) => (f.id === file.id ? { ...f, published: next } : f)));
    try {
      await audioApi.update(file.id, { published: next });
    } catch (err) {
      setFiles((fs) => fs.map((f) => (f.id === file.id ? { ...f, published: file.published } : f)));
      setError(err instanceof ApiError ? err.message : 'Could not update the program.');
    }
  }

  async function remove(file: AudioItem) {
    if (!window.confirm(`Delete "${file.title}"? Clients lose access permanently.`)) return;
    try {
      await audioApi.del(file.id);
      if (playingId === file.id) setPlayingId(null);
      await load();
      setNotice(`"${file.title}" deleted.`);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not delete the program.');
    }
  }

  const download = (id: string) => window.location.assign(downloadUrl(id));

  function kindCircle(file: AudioItem, locked = false) {
    return (
      <span
        style={{
          width: 44,
          height: 44,
          borderRadius: 'var(--radius-circle)',
          background: locked ? 'var(--surface-sunken)' : 'var(--navy-600)',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        <Icon name={locked ? 'lock' : audioKindIcon(file.kind)} size="20px" style={{ color: locked ? 'var(--navy-300)' : 'var(--white)' }} />
      </span>
    );
  }

  function caption(file: AudioItem): string {
    const parts = [AUDIO_KIND_LABELS[file.kind], formatAudioDuration(file.durationSec)];
    if (isManager) {
      parts.push(formatBytes(file.sizeBytes));
      parts.push(`${file.listeners ?? 0} ${file.listeners === 1 ? 'listener' : 'listeners'}`);
    }
    return parts.filter((p) => p !== '—').join(' · ');
  }

  // ------------------------------------------------------------------
  // Manager card
  // ------------------------------------------------------------------
  function managerCard(file: AudioItem) {
    return (
      <Card key={file.id} pad="sm">
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 'var(--space-4)' }}>
          {kindCircle(file)}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
              <span style={{ font: 'var(--type-h4)', color: 'var(--text-strong)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {file.title}
              </span>
              <Badge tone={file.published ? 'success' : 'neutral'}>{file.published ? 'Published' : 'Draft'}</Badge>
            </div>
            <div style={{ font: 'var(--type-caption)', color: 'var(--text-muted)', marginTop: 2 }}>{caption(file)}</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)', marginTop: 'var(--space-4)', flexWrap: 'wrap' }}>
              {file.kind === 'audible' && (
                <Button size="sm" variant="secondary" icon={playingId === file.id ? 'pause' : 'play'} onClick={() => setPlayingId(playingId === file.id ? null : file.id)}>
                  {playingId === file.id ? 'Pause' : 'Preview'}
                </Button>
              )}
              <Button size="sm" variant="ghost" icon="download" onClick={() => download(file.id)}>
                Download
              </Button>
              <Button size="sm" variant="ghost" onClick={() => void togglePublish(file)}>
                {file.published ? 'Unpublish' : 'Publish'}
              </Button>
              <span style={{ marginLeft: 'auto', display: 'inline-flex', gap: 'var(--space-2)' }}>
                <IconButton icon="pencil" label="Edit program" size="sm" onClick={() => setDialog({ mode: 'edit', file })} />
                <IconButton icon="trash-2" label="Delete program" size="sm" onClick={() => void remove(file)} />
              </span>
            </div>
          </div>
        </div>
      </Card>
    );
  }

  // ------------------------------------------------------------------
  // Client cards
  // ------------------------------------------------------------------
  function entitledCard(file: AudioItem) {
    return (
      <Card key={file.id} pad="sm">
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 'var(--space-4)' }}>
          {kindCircle(file)}
          <div style={{ flex: 1, minWidth: 0 }}>
            <span style={{ font: 'var(--type-h4)', color: 'var(--text-strong)' }}>{file.title}</span>
            <div style={{ font: 'var(--type-caption)', color: 'var(--text-muted)', marginTop: 2 }}>{caption(file)}</div>
            {file.kind === 'ultrasonic' && (
              <div style={{ font: 'var(--type-caption)', color: 'var(--text-muted)', marginTop: 'var(--space-2)' }}>
                Silent by design — for best results download and play on repeat through wired speakers; Bluetooth strips
                ultrasonic frequencies.
              </div>
            )}
            <div style={{ display: 'flex', gap: 'var(--space-3)', marginTop: 'var(--space-4)', flexWrap: 'wrap' }}>
              {file.kind === 'audible' ? (
                <>
                  <Button size="sm" icon={playingId === file.id ? 'pause' : 'play'} onClick={() => setPlayingId(playingId === file.id ? null : file.id)}>
                    {playingId === file.id ? 'Pause' : 'Play'}
                  </Button>
                  <Button size="sm" variant="ghost" icon="download" onClick={() => download(file.id)}>
                    Download
                  </Button>
                </>
              ) : (
                <>
                  <Button size="sm" icon="download" onClick={() => download(file.id)}>
                    Download
                  </Button>
                  <Button size="sm" variant="ghost" icon={playingId === file.id ? 'pause' : 'play'} onClick={() => setPlayingId(playingId === file.id ? null : file.id)}>
                    {playingId === file.id ? 'Pause' : 'Play'}
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      </Card>
    );
  }

  function lockedCard(file: AudioItem) {
    return (
      <Card key={file.id} pad="sm">
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 'var(--space-4)' }}>
          {kindCircle(file, true)}
          <div style={{ flex: 1, minWidth: 0 }}>
            <span style={{ font: 'var(--type-h4)', color: 'var(--text-strong)' }}>{file.title}</span>
            <div style={{ font: 'var(--type-caption)', color: 'var(--text-muted)', marginTop: 2 }}>{caption(file)}</div>
            <div style={{ marginTop: 'var(--space-4)' }}>
              <Button
                size="sm"
                variant="secondary"
                icon="external-link"
                onClick={() => window.open(file.storeUrl ?? 'https://zenmindware.com', '_blank', 'noopener')}
              >
                Buy on zenmindware.com
              </Button>
            </div>
          </div>
        </div>
      </Card>
    );
  }

  const grid = (items: AudioItem[], render: (f: AudioItem) => JSX.Element) => (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-6)' }}>{items.map(render)}</div>
  );

  const entitled = visible.filter((f) => f.entitled);
  const locked = visible.filter((f) => !f.entitled);

  return (
    <>
      {notice && (
        <Toast tone="success" title="Done" onDismiss={() => setNotice(null)}>
          {notice}
        </Toast>
      )}
      {error && (
        <Toast tone="danger" title="Something went wrong" onDismiss={() => setError(null)}>
          {error}
        </Toast>
      )}

      <Card pad="sm">
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-4)', flexWrap: 'wrap' }}>
          <Input icon="search" placeholder="Search the library" value={query} onChange={(e) => setQuery(e.target.value)} style={{ width: 240 }} />
          <Select
            value={kindFilter}
            onChange={(e) => setKindFilter(e.target.value as KindFilter)}
            options={[
              { value: 'all', label: 'All kinds' },
              ...(Object.keys(AUDIO_KIND_LABELS) as AudioKind[]).map((k) => ({ value: k, label: AUDIO_KIND_LABELS[k] })),
            ]}
            style={{ width: 180 }}
          />
          {isManager && (
            <>
              <Tag selected={publishedOnly} onClick={() => setPublishedOnly(!publishedOnly)}>
                Published
              </Tag>
              <span style={{ marginLeft: 'auto' }}>
                <Button size="sm" icon="plus" onClick={() => setDialog({ mode: 'create' })}>
                  New program
                </Button>
              </span>
            </>
          )}
        </div>
      </Card>

      {playing && (
        <AudioPlayback
          file={playing}
          subtitle={AUDIO_KIND_LABELS[playing.kind]}
          playing={playingId === playing.id}
          onToggle={() => setPlayingId(null)}
          onError={(message) => {
            setPlayingId(null);
            setError(message);
          }}
        />
      )}

      {loading ? (
        <Card>
          <div style={{ color: 'var(--text-muted)' }}>Loading…</div>
        </Card>
      ) : isManager ? (
        visible.length === 0 ? (
          <Card pad="none">
            <EmptyState
              icon="music"
              title="No programs yet"
              action={
                <Button size="sm" icon="plus" onClick={() => setDialog({ mode: 'create' })}>
                  New program
                </Button>
              }
            >
              Upload your first audio program — clients see it once it's published.
            </EmptyState>
          </Card>
        ) : (
          grid(visible, managerCard)
        )
      ) : (
        <>
          <Card pad="none" title="Your programs">
            {entitled.length === 0 ? (
              <EmptyState
                icon="headphones"
                title="Your library is empty"
                action={
                  <Button size="sm" variant="secondary" icon="external-link" onClick={() => window.open('https://zenmindware.com', '_blank', 'noopener')}>
                    Open the store
                  </Button>
                }
              >
                Programs you purchase on zenmindware.com, or that your practitioner shares with you, appear here.
              </EmptyState>
            ) : (
              <div style={{ padding: 'var(--space-5)' }}>{grid(entitled, entitledCard)}</div>
            )}
          </Card>
          {locked.length > 0 && (
            <Card pad="none" title="More from Zen Mindware">
              <div style={{ padding: 'var(--space-5)' }}>{grid(locked, lockedCard)}</div>
            </Card>
          )}
        </>
      )}

      {dialog && (
        <ProgramDialog
          mode={dialog.mode}
          file={dialog.mode === 'edit' ? dialog.file : undefined}
          onClose={() => setDialog(null)}
          onSaved={(message) => {
            setDialog(null);
            setNotice(message);
            void load();
          }}
        />
      )}
    </>
  );
}
